import React from 'react';
import { observer } from 'mobx-react-lite';
import Button from '../components/button';
import { AppPage } from '../components/appPage';
import { appService } from '../services/appService';
import { roomStore } from '../stores/roomStore';
import { appLocalStore } from '../stores/appLocalStore';
import { urlOptions } from '../utils/urlOptions';
import { storageService } from '../services/storageService';
import { svgIcons } from '../svgIcons';

const JoinRoom: React.FC = observer(() => {
  const [joinCode, setJoinCode] = React.useState('');

  // Load saved join code on mount
  React.useEffect(() => {
    const urlJoinCode = urlOptions.join?.toUpperCase();
    const savedJoinCode = storageService.getJoinCode();
    
    if (urlJoinCode) {
      setJoinCode(urlJoinCode);
    } else if (savedJoinCode) {
      setJoinCode(savedJoinCode);
    }
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && joinCode.trim()) {
      appService.joinRoom(joinCode);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear error when user types
    roomStore.clearJoinError();
    setJoinCode(e.target.value.toUpperCase());
  };

  const handleClear = () => {
    setJoinCode('');
    roomStore.clearJoinError();
  };

  return (
    <AppPage
      className="app--bgBlue"
      content={
        <div className="bg-[var(--bg-primary)] p-10 rounded-2xl shadow-2xl max-w-md w-[90%]">
          <h1 className="text-[var(--text-primary)] text-3xl font-bold m-0 mb-2 text-center">Join a Game</h1>
          <p className="text-[var(--text-secondary)] text-base m-0 mb-8 text-center">Enter the joining code</p>
          
          <div className="flex flex-col gap-4">
            <div className="relative">
              <input
                type="text"
                className={`px-4 py-3 text-xl font-semibold tracking-[0.25rem] text-center uppercase border-2 ${
                  roomStore.joinError ? 'border-red-700' : 'border-[var(--input-border)]'
                } rounded-lg outline-none transition-colors bg-[var(--input-bg)] text-[var(--text-primary)] focus:border-[var(--input-border-focus)] placeholder:text-[var(--text-secondary)] placeholder:tracking-normal placeholder:normal-case placeholder:text-base placeholder:font-normal w-full`}
                placeholder="Enter 6-character code"
                value={joinCode}
                onChange={handleCodeChange}
                onKeyUp={handleKeyPress}
                maxLength={6}
                autoFocus
              />
              {joinCode && (
                <button
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClear();
                    }
                  }}
                  tabIndex={0}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors cursor-pointer bg-transparent border-none"
                  aria-label="Clear join code"
                >
                  {svgIcons.xIcon()}
                </button>
              )}
            </div>
            
            {roomStore.joinError && <div className="text-red-700 text-sm text-center -mt-2">{roomStore.joinError}</div>}
            
            <div className="flex flex-col gap-3 mt-2">
              <Button 
                onClick={() => appService.joinRoom(joinCode)}
                disabled={!joinCode.trim() || roomStore.isJoiningRoom}
                fullWidth
              >
                {roomStore.isJoiningRoom ? 'Connecting...' : 'Connect'}
              </Button>
              
              <Button 
                onClick={() => appLocalStore.navigateToPage("lobby")}
                variant="secondary"
                fullWidth
              >
                Back
              </Button>
            </div>
          </div>
        </div>
      }
    />
  );
});

export default JoinRoom;
